'use client';

import React, { useRef, useState } from 'react';

import type {
  BookPayload,
  CardLabels,
  EnquirePayload,
  FlatPackageComponent,
} from '../../types';
import Cards from '../PackageCard';

interface RowItem {
  pkg: FlatPackageComponent;
  id: string;
  isFirst?: boolean;
}

interface PackageRowProps {
  row: RowItem[];
  categoryTitle: string;
  labels?: CardLabels;
  onEnquire: (payload: EnquirePayload) => void;
  onBook?: (payload: BookPayload) => void;
}

export default function PackageRow({
  row,
  categoryTitle,
  labels,
  onEnquire,
  onBook,
}: PackageRowProps): React.ReactElement {
  const [openCardIds, setOpenCardIds] = useState<Set<string>>(() => {
    // Mirror `defaultOpen` below: if a card in this row starts open, the
    // row must start in items-start too, or it'll wrongly stretch siblings
    // to match a card whose real height it doesn't know about yet.
    const firstOpenId = row.find((item) => item.isFirst)?.id;
    return firstOpenId ? new Set([firstOpenId]) : new Set();
  });
  const [frozenHeight, setFrozenHeight] = useState<number | null>(null);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const handleCardToggle = (id: string, isOpen: boolean): void => {
    const wasAnyOpen = openCardIds.size > 0;

    if (!wasAnyOpen && isOpen) {
      // A card in this row is about to expand for the first time — snapshot
      // the row's current (uniformly stretched) height before the layout
      // changes, so the untouched siblings can be pinned to it instead of
      // shrinking down to their own natural size once stretch is dropped.
      const heights = row
        .map(({ id: rowId }) => cardRefs.current.get(rowId)?.offsetHeight)
        .filter((h): h is number => typeof h === 'number');
      if (heights.length) setFrozenHeight(Math.max(...heights));
    }

    setOpenCardIds((prev) => {
      const next = new Set(prev);
      if (isOpen) next.add(id);
      else next.delete(id);
      if (next.size === 0) setFrozenHeight(null);
      return next;
    });
  };

  const rowHasOpen = openCardIds.size > 0;

  return (
    <div
      className={`grid grid-cols-1 gap-6 md:grid-cols-3 ${rowHasOpen ? 'items-start' : 'items-stretch'}`}
    >
      {row.map(({ pkg, id, isFirst }) => {
        const isThisOpen = openCardIds.has(id);
        const shouldFreeze = rowHasOpen && !isThisOpen && frozenHeight !== null;

        return (
          <div
            key={id}
            ref={(el) => {
              if (el) cardRefs.current.set(id, el);
              else cardRefs.current.delete(id);
            }}
            className="break-inside-avoid pb-6"
            style={
              shouldFreeze
                ? { height: frozenHeight ?? undefined, overflow: 'hidden' }
                : undefined
            }
          >
            <Cards
              badge={categoryTitle}
              title={pkg?.title}
              tests={pkg?.tests}
              testGroups={pkg?.testGroups}
              descriptionText={pkg?.descriptionText}
              text4={pkg?.text4}
              disclaimer={pkg?.disclaimer}
              button={pkg?.button}
              testType={pkg?.testType}
              gender={pkg?.gender}
              price={pkg?.price}
              defaultOpen={isFirst}
              labels={labels}
              onToggleOpen={(isOpen) => handleCardToggle(id, isOpen)}
              onEnquire={() =>
                onEnquire({
                  category: categoryTitle,
                  title: pkg?.title,
                  gender: pkg?.gender,
                  testType: pkg?.testType,
                  price: pkg?.price,
                } as EnquirePayload)
              }
              onBook={
                pkg?.apiIds?.length
                  ? () =>
                      onBook?.({
                        category: categoryTitle,
                        title: pkg?.title,
                        gender: pkg?.gender,
                        testType: pkg?.testType,
                        price: pkg?.price,
                        apiIds: pkg.apiIds,
                        depositFee: pkg.depositFee,
                      } as BookPayload)
                  : undefined
              }
            />
          </div>
        );
      })}
    </div>
  );
}
